import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import api from 'api';
import usePaginatedRequest from 'hooks/usePaginatedRequest';
import useQueryParams from 'hooks/useQueryParams';
import Table from 'components/Table';
import Pagination from 'components/Pagination';
import { columns, contentTransformations, headingTranslations } from './data';
import { USEERS_ROUTER_LIMIT } from '../../Users.router';
import { Button, Select } from 'ncoded-component-library';
import AddUserModal from '../../components/AddUserModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import CurrentUserContext from 'providers/CurrentUser';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './Managers.styles.scss';
import './Managers.styles.responsive.scss';

const LIMIT_FLEET = 1000;

type ManagersProps = {
  className?: string;
};

const Managers: React.FC<ManagersProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { params, setQueryParam, removeQueryParam } = useQueryParams();
  const [fleetFilterOptions, setFleetFilterOptions] = useState([]);
  const { isAdmin } = useContext(CurrentUserContext);
  const { popError } = useContext(PopNotificationsContext);

  const request = useMemo(() => api.user.managers.getManagers, []);

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      try {
        const {
          data: { items },
        } = await api.fleet.getFleets({ limit: `${LIMIT_FLEET}` });

        const createSelectOptions = items.map((fleet) => ({
          value: fleet._id,
          label: fleet.name,
        }));
        setFleetFilterOptions([
          { value: '', label: t('none') },
          ...createSelectOptions,
        ]);
      } catch (e: any) {
        popError(e);
      }
    })();
  }, [isAdmin, popError, t]);

  const {
    totalPages,
    isLoading,
    items: managers,
    setItems,
  } = usePaginatedRequest({
    getItems: request,
    defaultParams: {
      limit: USEERS_ROUTER_LIMIT,
      ...(params.filter ? { filter: JSON.parse(params.filter) } : {}),
      populate: '["fleet"]',
    },
    dependencies: [params, request],
  });

  const classes = classNames('managers', className);

  return (
    <div className={classes}>
      <div className="users__heading">
        <p className="fm-page-title user-title">{t('managers')}</p>
        <div className="heading__menu">
          {!!fleetFilterOptions.length && (
            <Select
              value={params.filter ? JSON.parse(params.filter).fleetId : ''}
              onChange={(opt: any) => {
                if (opt.value)
                  setQueryParam(
                    'filter',
                    JSON.stringify({ fleetId: opt.value }),
                  );
                else {
                  removeQueryParam('filter');
                }
              }}
              placeholder={t('selectFleet')}
              options={fleetFilterOptions}
            />
          )}
          <Button onClick={() => modal.open()}>{t('addManager')}</Button>
        </div>
      </div>
      <Table
        data={managers}
        columns={columns}
        headingTranslations={headingTranslations}
        isDataLoading={isLoading}
        tableContentTransformations={contentTransformations}
        updateTableData={setItems}
      />
      <Pagination
        lastPage={+totalPages}
        currentPage={+params.page || 1}
        setPage={(n) => setQueryParam('page', n)}
      />
      <AddUserModal
        ref={modalRef}
        withFleetName
        addUserRequest={api.user.managers.addManager}
        onSuccess={setItems}
      />
    </div>
  );
};

export default Managers;
